"use strict";
/**
 * Admin-configurable pricing config, as a value.
 *
 * See `docs/adr/0003-pricing-config-as-parameter.md`. Every rate the engines
 * need now arrives as a parameter instead of being read off a module-level
 * constant, because the three surfaces that price a booking get their config
 * from three different places: the API server loads it live from Postgres, and
 * the booking app and dashboard fetch it once from the API at page load. A fixed
 * `RATE_CARDS` import only works while there is exactly one baked-in source of
 * truth, and the moment there isn't, the pressure is to reimplement the formula
 * somewhere the config *is* reachable — which is how the same numbers ended up
 * in five files the first time.
 *
 * Two properties this module is built to guarantee:
 *
 *   - **The default config is today's behaviour, exactly.** Every surge axis
 *     defaults to zero and Same-Day defaults to off, so threading a config
 *     parameter through the engines changes no price until an admin sets one.
 *     A refactor that quietly moves a total is indistinguishable from a pricing
 *     bug, and this one moves nothing.
 *   - **Nothing here reads the clock.** Not the current time, not the local
 *     timezone. See `docs/adr/0004-same-day-availability-server-side-only.md`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parsePricingConfig = exports.fromPublicPricingConfig = exports.withProximityTier = exports.withSameDayForDate = exports.pricingConfigFingerprint = exports.toPublicPricingConfig = exports.copyTimeOfDayRules = exports.serializePricingConfig = exports.assertValidPricingConfig = exports.dayOfWeekForServiceDate = exports.withoutDayOfWeekVariation = exports.pricingVariesByDayOfWeek = exports.hasAnySurge = exports.defaultPricingConfig = exports.zeroSurgeConfig = exports.noProximityConfig = exports.noTimeOfDayRules = exports.windowOffersDay = exports.perDay = exports.DAYS_OF_WEEK = void 0;
const money_1 = require("./money");
const rates_1 = require("./rates");
exports.DAYS_OF_WEEK = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];
function perDay(value) {
    return {
        sunday: value,
        monday: value,
        tuesday: value,
        wednesday: value,
        thursday: value,
        friday: value,
        saturday: value,
    };
}
exports.perDay = perDay;
/**
 * Whether a window is on offer on a given weekday.
 *
 * The one place the absent-means-everything defaults are read, so the engine,
 * the API projection and the admin preview cannot drift on what a window with
 * no `days` key means.
 */
function windowOffersDay(window, day) {
    if (window.enabled === false)
        return false;
    if (!Array.isArray(window.days))
        return true;
    return window.days.includes(day);
}
exports.windowOffersDay = windowOffersDay;
/**
 * No rules on any day, which is how the axis ships.
 *
 * Not `perDay([])`: that hands all seven days the *same* array, so pushing a
 * rule onto Friday would quietly add it to every other day as well.
 */
function noTimeOfDayRules() {
    return {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
    };
}
exports.noTimeOfDayRules = noTimeOfDayRules;
/** Both proximity outcomes switched off, which is how the feature ships. */
function noProximityConfig() {
    return {
        clusterEnabled: false,
        travelEnabled: false,
        clusterDiscountPercent: 0,
        travelPremiumPercent: 0,
    };
}
exports.noProximityConfig = noProximityConfig;
/**
 * Surge axes all zero, Same-Day off, proximity off, Flexible at its current flat
 * $10.
 */
function zeroSurgeConfig(rateCard) {
    return {
        rateCard,
        dayOfWeekSurgePercent: perDay(0),
        sameDaySurchargePercent: rates_1.CLEANING_TYPE_IDS.reduce((acc, id) => {
            acc[id] = perDay(0);
            return acc;
        }, {}),
        flexibleDiscountCents: perDay(rates_1.FLEXIBLE_DISCOUNT_CENTS),
        sameDay: {
            enabledByDay: perDay(false),
            cutoffMinutesByDay: perDay(0),
            windows: [],
        },
        proximity: noProximityConfig(),
        timeOfDayRules: noTimeOfDayRules(),
    };
}
exports.zeroSurgeConfig = zeroSurgeConfig;
/**
 * The config an un-migrated caller gets: the hardcoded rate card for this
 * instant, and no surge at all.
 *
 * This is what makes the config parameter safe to add everywhere at once. A call
 * site that has not been taught to fetch config from the API prices exactly as
 * it did before, rather than pricing at zero or throwing.
 */
function defaultPricingConfig(rateCardInstant) {
    return zeroSurgeConfig((0, rates_1.getRateCard)(rateCardInstant));
}
exports.defaultPricingConfig = defaultPricingConfig;
/** Whether any surge-family axis is actually set on this config. */
function hasAnySurge(config) {
    const dayOfWeek = exports.DAYS_OF_WEEK.some((day) => config.dayOfWeekSurgePercent[day] !== 0);
    const sameDay = rates_1.CLEANING_TYPE_IDS.some((id) => exports.DAYS_OF_WEEK.some((day) => { var _a, _b; return ((_b = (_a = config.sameDaySurchargePercent[id]) === null || _a === void 0 ? void 0 : _a[day]) !== null && _b !== void 0 ? _b : 0) !== 0; }));
    return dayOfWeek || sameDay;
}
exports.hasAnySurge = hasAnySurge;
/**
 * Whether pricing on this config can differ by the service date's day of week.
 *
 * The gate on requiring a service date. Making it mandatory outright would break
 * every existing caller — the landing page's "Book from $X" button has no
 * service date to give, and it is a real price. Making it optional
 * unconditionally would let a surge-bearing config silently price a booking at
 * the wrong day's rate. So: required exactly when the answer could differ.
 */
function pricingVariesByDayOfWeek(config) {
    if (hasAnySurge(config))
        return true;
    const flexible = exports.DAYS_OF_WEEK.map((day) => config.flexibleDiscountCents[day]);
    return flexible.some((cents) => cents !== flexible[0]);
}
exports.pricingVariesByDayOfWeek = pricingVariesByDayOfWeek;
/**
 * The same config with every day-dependent axis collapsed, so it can price a
 * booking that has no service date yet.
 *
 * For the funnel's early steps. A customer picks bedrooms before they pick a
 * date, and the cart has to show *something* — but `resolveDayOfWeek` refuses to
 * price a day-varying config without a date, and rightly so. The answer is not to
 * relax that rule but to hand it a config on which the question does not arise:
 * this returns one where `pricingVariesByDayOfWeek` is false by construction, so
 * the engine takes its dateless path instead of throwing.
 *
 * The rate card is untouched. Only the axes that genuinely cannot be resolved
 * without a date are neutralised, so an admin's base-price edit still reaches the
 * cart on the very first step — which is the entire point of serving config to
 * the clients.
 *
 * Surge and Same-Day surcharge go to zero: there is no honest way to charge a
 * day's surge before the customer has chosen the day. The resulting price is
 * therefore a *lower* bound on the surge axis, which is why callers must present
 * it as a "from" price rather than a quote.
 *
 * The Flexible Time discount collapses to the **smallest** value across the week,
 * not the largest. Both remove the variance; the smallest is the one that cannot
 * overstate the discount, so the displayed price is never lowered by a better
 * day's deal that this booking may not get. Understating the surge is covered by
 * the "from" label; overstating a discount would not be.
 */
function withoutDayOfWeekVariation(config) {
    const flexibleFloor = Math.min(...exports.DAYS_OF_WEEK.map((day) => config.flexibleDiscountCents[day]));
    return Object.assign(Object.assign({}, config), { dayOfWeekSurgePercent: perDay(0), sameDaySurchargePercent: rates_1.CLEANING_TYPE_IDS.reduce((acc, id) => {
            acc[id] = perDay(0);
            return acc;
        }, {}), flexibleDiscountCents: perDay(flexibleFloor) });
}
exports.withoutDayOfWeekVariation = withoutDayOfWeekVariation;
/**
 * The day of the week a service date falls on.
 *
 * Takes the calendar date apart by hand instead of calling `new Date(value)`,
 * which is the bug this function exists to prevent: `new Date("2026-08-16")` is
 * parsed as **UTC** midnight, and `.getDay()` on it in New York reads back
 * Saturday the 15th. Every Sunday booking would have been surged at Saturday's
 * rate — the exact class of timezone error `V5_FROM`'s comment and ADR 0004 are
 * both about.
 *
 * Accepts both stored shapes: `"2026-08-16"` and `"2026-08-16T14:00"`. The
 * dashboard writes the second (`apps/dashboard/utils/form.ts` splits and
 * re-concatenates a time onto `clean_date`), so refusing it would break editing
 * a booking from the admin UI.
 */
function dayOfWeekForServiceDate(serviceDate) {
    const datePart = String(serviceDate !== null && serviceDate !== void 0 ? serviceDate : "").trim().split("T")[0];
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (!match) {
        throw new RangeError(`Service date must be a YYYY-MM-DD calendar date, got ${JSON.stringify(serviceDate)}. Surge is resolved from the day of the week the cleaning happens, and ` +
            `guessing that from an unparseable value would surge the wrong day.`);
    }
    const [, year, month, day] = match;
    const asUtc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    // Read back with getUTCDay: the components went in as UTC, so this is the
    // weekday of the calendar date itself, with no timezone applied to it.
    return exports.DAYS_OF_WEEK[asUtc.getUTCDay()];
}
exports.dayOfWeekForServiceDate = dayOfWeekForServiceDate;
/**
 * Reject a config that cannot be applied, at the point it enters the process,
 * rather than mid-calculation.
 *
 * A surge percentage arrives from an admin form and then from a database row, so
 * it has had two chances to be nonsense by the time an engine sees it. A
 * negative "surge" would quietly discount, and a 10000% one would look like a
 * successful charge for the wrong amount.
 */
/**
 * One day's time-of-day rules, checked at the boundary.
 *
 * The engine refuses these too, but a config is validated where it is built and
 * parsed -- so a bad admin save is rejected as it is written rather than the
 * next time somebody tries to price a booking on that day, which is a failure
 * in front of a customer instead of in front of the person who caused it.
 */
function assertTimeOfDayRules(rules, day) {
    if (rules === undefined)
        return;
    if (!Array.isArray(rules)) {
        throw new RangeError(`${day} time-of-day rules must be a list, got ${String(rules)}`);
    }
    for (const rule of rules) {
        assertMinuteOfDay(rule === null || rule === void 0 ? void 0 : rule.startMinutes, `${day} time-of-day rule start`);
        // End is exclusive, so midnight-to-midnight (1440) is a legal whole day.
        if (!Number.isInteger(rule === null || rule === void 0 ? void 0 : rule.endMinutes) || rule.endMinutes < 0 || rule.endMinutes > 1440) {
            throw new RangeError(`${day} time-of-day rule end must be a whole minute of the day, got ${String(rule === null || rule === void 0 ? void 0 : rule.endMinutes)}`);
        }
        if (rule.endMinutes <= rule.startMinutes) {
            throw new RangeError(`A ${day} time-of-day rule ends at or before it starts (${rule.startMinutes} ` +
                `to ${rule.endMinutes}). A span covering no minute prices nothing, which ` +
                `reads as the rule simply not applying.`);
        }
        assertSurgePercent(Math.abs(rule.percent), `${day} time-of-day rule`);
    }
    for (let i = 0; i < rules.length; i++) {
        for (let j = i + 1; j < rules.length; j++) {
            const a = rules[i];
            const b = rules[j];
            if (a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes) {
                throw new RangeError(`Two ${day} time-of-day rules overlap (${a.startMinutes}-${a.endMinutes} and ` +
                    `${b.startMinutes}-${b.endMinutes}). One arrival would match both, and ` +
                    `charging either is a coin toss.`);
            }
        }
    }
}
function assertValidPricingConfig(config) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    if (!config || !config.rateCard) {
        throw new RangeError("Pricing config must include a rate card");
    }
    assertRoomPrices(config.rateCard.roomCents);
    for (const day of exports.DAYS_OF_WEEK) {
        assertSurgePercent((_a = config.dayOfWeekSurgePercent) === null || _a === void 0 ? void 0 : _a[day], `${day} surge`);
        const flexible = (_b = config.flexibleDiscountCents) === null || _b === void 0 ? void 0 : _b[day];
        if (!Number.isInteger(flexible) || flexible < 0) {
            throw new RangeError(`${day} Flexible Time discount must be a whole number of cents >= 0, got ${String(flexible)}`);
        }
        for (const id of rates_1.CLEANING_TYPE_IDS) {
            assertSurgePercent((_d = (_c = config.sameDaySurchargePercent) === null || _c === void 0 ? void 0 : _c[id]) === null || _d === void 0 ? void 0 : _d[day], `${id} ${day} Same-Day surcharge`);
        }
        assertMinuteOfDay((_f = (_e = config.sameDay) === null || _e === void 0 ? void 0 : _e.cutoffMinutesByDay) === null || _f === void 0 ? void 0 : _f[day], `${day} Same-Day cutoff`);
        assertTimeOfDayRules((_g = config.timeOfDayRules) === null || _g === void 0 ? void 0 : _g[day], day);
    }
    assertSurgePercent((_h = config.proximity) === null || _h === void 0 ? void 0 : _h.clusterDiscountPercent, "nearby-cleaning discount");
    assertSurgePercent((_j = config.proximity) === null || _j === void 0 ? void 0 : _j.travelPremiumPercent, "travel premium");
    for (const window of (_l = (_k = config.sameDay) === null || _k === void 0 ? void 0 : _k.windows) !== null && _l !== void 0 ? _l : []) {
        assertMinuteOfDay(window.startMinutes, `window ${window.id} start`);
        assertMinuteOfDay(window.endMinutes, `window ${window.id} end`);
        assertMinuteOfDay(window.cutoffMinutes, `window ${window.id} cutoff`);
        if (window.endMinutes <= window.startMinutes) {
            throw new RangeError(`Arrival window ${window.id} ends at or before it starts (${window.startMinutes}-${window.endMinutes})`);
        }
        /**
         * `enabled` and `days` are deliberately not validated here.
         *
         * This function runs on *read* as well as on write — `loadCurrentPricingConfig`
         * and `parsePricingConfig` both call it — so a rule added here turns a bad
         * stored row into a throw inside the pricing endpoint, and one window nobody
         * can book into a funnel nobody can book. A window scheduled on no days, or
         * on a string that is not a weekday, is simply never offered; that is the
         * same containment the cutoff clamp in `resolveSameDayAvailability` uses.
         * The mutation rejects both, which is where a human is present to be told.
         */
    }
    return config;
}
exports.assertValidPricingConfig = assertValidPricingConfig;
/**
 * A full room table: `MAX_ROOMS` whole-cent prices for each of bedrooms and
 * bathrooms.
 *
 * Exactly that many, not "at least one". A short table leaves the funnel's
 * larger homes with no price, and the engine refuses them — so a table missing
 * its fifth bathroom is every five-bathroom booking failing at checkout, while
 * the cause sits in config nobody is looking at. The upper bound is loose on
 * purpose, as `setRateCard`'s is: it catches a cents/dollars slip, not a
 * pricing opinion.
 */
function assertRoomPrices(prices) {
    for (const kind of ["bedroom", "bathroom"]) {
        const table = prices === null || prices === void 0 ? void 0 : prices[kind];
        if (!Array.isArray(table) || table.length !== rates_1.MAX_ROOMS) {
            throw new RangeError(`The ${kind} price table must have exactly ${rates_1.MAX_ROOMS} entries, got ` +
                `${Array.isArray(table) ? table.length : String(table)}`);
        }
        table.forEach((cents, index) => {
            if (!Number.isInteger(cents) || cents < 0 || cents > 1000000) {
                throw new RangeError(`${kind} ${index + 1} must be priced between $0 and $10,000 in whole ` +
                    `cents, got ${String(cents)}`);
            }
        });
    }
    return prices;
}
/** A surge percentage: 0-100, at most two decimal places. */
function assertSurgePercent(percent, label) {
    if (percent === undefined || percent === null) {
        throw new RangeError(`${label} is missing from the pricing config`);
    }
    (0, money_1.percentToBasisPoints)(percent, label);
    if (percent < 0 || percent > 100) {
        throw new RangeError(`${label} must be between 0 and 100, got ${percent}`);
    }
    return percent;
}
/** Minutes from business-local midnight: 0 through 1440. */
function assertMinuteOfDay(value, label) {
    if (!Number.isInteger(value) || value < 0 || value > 1440) {
        throw new RangeError(`${label} must be a whole number of minutes from midnight (0-1440), got ${String(value)}`);
    }
    return value;
}
function serializePricingConfig(config) {
    return Object.assign(Object.assign({}, config), { rateCard: Object.assign(Object.assign({}, config.rateCard), { effectiveFrom: config.rateCard.effectiveFrom.toISOString() }) });
}
exports.serializePricingConfig = serializePricingConfig;
/**
 * Narrow a server-side config to the publishable projection.
 *
 * Written as an explicit field-by-field construction rather than a spread with
 * deletions, so a field added to `PricingConfig` later is *not* published by
 * default. A leak by omission is exactly the failure mode here — the axis that
 * gets added and quietly shipped is the one nobody reviewed.
 */
/**
 * A deep copy of a day's rules, treating a missing or malformed list as no
 * rules. A config serialized before this axis existed has no key at all, and a
 * reader that threw on it would refuse to price every pre-existing booking.
 */
function copyTimeOfDayRules(rules) {
    const copy = noTimeOfDayRules();
    if (!rules)
        return copy;
    for (const day of exports.DAYS_OF_WEEK) {
        const list = rules[day];
        if (!Array.isArray(list))
            continue;
        copy[day] = list.map((rule) => ({
            startMinutes: rule.startMinutes,
            endMinutes: rule.endMinutes,
            percent: rule.percent,
        }));
    }
    return copy;
}
exports.copyTimeOfDayRules = copyTimeOfDayRules;
function toPublicPricingConfig(config) {
    return {
        rateCard: {
            version: config.rateCard.version,
            effectiveFrom: config.rateCard.effectiveFrom.toISOString(),
            cleaningTypes: Object.assign({}, config.rateCard.cleaningTypes),
            /**
             * Published, like the cleaning-type prices beside it and for the same
             * reason (ADR 0006): a customer prices every room count by changing the
             * form and reading the total, so there is nothing to hide — and the cart
             * cannot show a five-bedroom price without it.
             */
            roomCents: {
                bedroom: [...config.rateCard.roomCents.bedroom],
                bathroom: [...config.rateCard.roomCents.bathroom],
            },
            supportsDecimals: config.rateCard.supportsDecimals,
        },
        dayOfWeekSurgePercent: Object.assign({}, config.dayOfWeekSurgePercent),
        flexibleDiscountCents: Object.assign({}, config.flexibleDiscountCents),
        timeOfDayRules: copyTimeOfDayRules(config.timeOfDayRules),
        configVersion: pricingConfigFingerprint(config),
    };
}
exports.toPublicPricingConfig = toPublicPricingConfig;
/**
 * A short, stable fingerprint of every value that can move a price.
 *
 * Not a hash for security — it is a change detector, and it is compared against
 * a value this same function produced on the same server minutes earlier. What
 * it must guarantee is that any admin edit changes it, which is why it walks the
 * full config rather than the public projection: a Same-Day surcharge edit is
 * invisible in the projection and very much visible on the customer's card.
 *
 * Canonical by construction — every key is written in a fixed order rather than
 * relying on `JSON.stringify` key ordering — so two servers holding the same
 * config always produce the same string, and a config that only differs by
 * property insertion order never looks like a change.
 */
function pricingConfigFingerprint(config) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
    const parts = [
        config.rateCard.version,
        config.rateCard.effectiveFrom.toISOString(),
        String(config.rateCard.supportsDecimals),
    ];
    for (const id of rates_1.CLEANING_TYPE_IDS) {
        parts.push(`ct:${id}=${(_a = config.rateCard.cleaningTypes[id]) !== null && _a !== void 0 ? _a : "-"}`);
    }
    parts.push(`rb:${((_c = (_b = config.rateCard.roomCents) === null || _b === void 0 ? void 0 : _b.bedroom) !== null && _c !== void 0 ? _c : []).join(",")}`);
    parts.push(`ra:${((_e = (_d = config.rateCard.roomCents) === null || _d === void 0 ? void 0 : _d.bathroom) !== null && _e !== void 0 ? _e : []).join(",")}`);
    for (const day of exports.DAYS_OF_WEEK) {
        parts.push(`dow:${day}=${(_g = (_f = config.dayOfWeekSurgePercent) === null || _f === void 0 ? void 0 : _f[day]) !== null && _g !== void 0 ? _g : "-"}`);
        parts.push(`flex:${day}=${(_j = (_h = config.flexibleDiscountCents) === null || _h === void 0 ? void 0 : _h[day]) !== null && _j !== void 0 ? _j : "-"}`);
        parts.push(`sde:${day}=${((_l = (_k = config.sameDay) === null || _k === void 0 ? void 0 : _k.enabledByDay) === null || _l === void 0 ? void 0 : _l[day]) ? 1 : 0}`);
        parts.push(`sdc:${day}=${(_p = (_o = (_m = config.sameDay) === null || _m === void 0 ? void 0 : _m.cutoffMinutesByDay) === null || _o === void 0 ? void 0 : _o[day]) !== null && _p !== void 0 ? _p : "-"}`);
        for (const id of rates_1.CLEANING_TYPE_IDS) {
            parts.push(`sds:${id}:${day}=${(_s = (_r = (_q = config.sameDaySurchargePercent) === null || _q === void 0 ? void 0 : _q[id]) === null || _r === void 0 ? void 0 : _r[day]) !== null && _s !== void 0 ? _s : "-"}`);
        }
    }
    parts.push(`prx:ce=${((_t = config.proximity) === null || _t === void 0 ? void 0 : _t.clusterEnabled) ? 1 : 0}`);
    parts.push(`prx:te=${((_u = config.proximity) === null || _u === void 0 ? void 0 : _u.travelEnabled) ? 1 : 0}`);
    parts.push(`prx:cd=${(_w = (_v = config.proximity) === null || _v === void 0 ? void 0 : _v.clusterDiscountPercent) !== null && _w !== void 0 ? _w : "-"}`);
    parts.push(`prx:tp=${(_y = (_x = config.proximity) === null || _x === void 0 ? void 0 : _x.travelPremiumPercent) !== null && _y !== void 0 ? _y : "-"}`);
    /**
     * Time-of-day rules, sorted by start so a reordered list is not a config
     * change. In the fingerprint because an admin editing a rule between the
     * customer reading their total and submitting it must send the booking back
     * for a re-quote -- which is the entire job of this string.
     */
    for (const day of exports.DAYS_OF_WEEK) {
        const rules = [...((_0 = (_z = config.timeOfDayRules) === null || _z === void 0 ? void 0 : _z[day]) !== null && _0 !== void 0 ? _0 : [])].sort((a, b) => a.startMinutes - b.startMinutes);
        for (const rule of rules) {
            parts.push(`tod:${day}=${rule.startMinutes}|${rule.endMinutes}|${rule.percent}`);
        }
    }
    // Sorted by id so a reordered `windows` array is not a config change.
    const windows = [...((_2 = (_1 = config.sameDay) === null || _1 === void 0 ? void 0 : _1.windows) !== null && _2 !== void 0 ? _2 : [])].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    for (const window of windows) {
        parts.push(`win:${window.id}=${window.label}|${window.startMinutes}|${window.endMinutes}` +
            `|${window.cutoffMinutes}|${window.enabled === false ? 0 : 1}` +
            `|${((_3 = window.days) !== null && _3 !== void 0 ? _3 : []).slice().sort().join(",")}`);
    }
    return fnv1a(parts.join(";"));
}
exports.pricingConfigFingerprint = pricingConfigFingerprint;
/**
 * FNV-1a, 32 bits, run twice over the same input with different offsets and
 * concatenated — 64 bits of hex in a dozen lines, with no dependency and no
 * `crypto` import, because this package runs in the browser bundle as well as on
 * the server. Collisions at this width are not a practical concern for a value
 * whose only job is to differ when a config differs.
 */
function fnv1a(input) {
    const hash = (offset) => {
        let h = offset;
        for (let i = 0; i < input.length; i++) {
            h ^= input.charCodeAt(i);
            // The FNV prime, 16777619, by shift-and-add: `h * 16777619` overflows the
            // exactly-representable range and would lose low bits.
            h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
        }
        return h.toString(16).padStart(8, "0");
    };
    return hash(0x811c9dc5) + hash(0x01000193);
}
/**
 * Enable Same-Day for one service date, at a surcharge the server resolved.
 *
 * The client cannot price a Same-Day booking from published config, deliberately:
 * ADR 0006 withholds the surcharge grid, and the engine refuses a Same-Day booking
 * on a day its config does not offer. But a customer who has just been *offered*
 * Same-Day needs to see what it costs, and the availability response already
 * carries the surcharge for the one date they asked about — which is a single cell,
 * not the grid.
 *
 * So the client folds that answer back into its config and prices through the
 * ordinary path, rather than being handed a separate "add this many cents" input.
 * Keeping the engine config-driven is what stops a second, parallel way of
 * computing a total existing on the client — the failure mode this package was
 * built to end.
 *
 * Only the named day is enabled, and no window cutoffs are added: whether the
 * window is still open stays the server's answer.
 */
function withSameDayForDate(config, serviceDate, surchargePercent, cleaningType) {
    const day = dayOfWeekForServiceDate(serviceDate);
    return assertValidPricingConfig(Object.assign(Object.assign({}, config), { sameDaySurchargePercent: Object.assign(Object.assign({}, config.sameDaySurchargePercent), { [cleaningType]: Object.assign(Object.assign({}, config.sameDaySurchargePercent[cleaningType]), { [day]: surchargePercent }) }), sameDay: Object.assign(Object.assign({}, config.sameDay), { enabledByDay: Object.assign(Object.assign({}, config.sameDay.enabledByDay), { [day]: true }), 
            /**
             * A full day, because this config is used only to price a booking the server
             * has already said is offerable. It is not used to decide availability —
             * nothing on the client is.
             */
            cutoffMinutesByDay: Object.assign(Object.assign({}, config.sameDay.cutoffMinutesByDay), { [day]: 1440 }) }) }));
}
exports.withSameDayForDate = withSameDayForDate;
/**
 * Give one booking the proximity tier the server resolved for it.
 *
 * The counterpart to `withSameDayForDate`, and it exists for the same reason: a
 * customer who has just been *told* they are getting a nearby-cleaning discount
 * needs to see what it is worth, but the client cannot derive it. Density is
 * other people's bookings, and ADR 0009 keeps that off the wire — the server
 * returns the resolved tier and the single percentage that applies to it.
 *
 * Folding that answer back into the config, rather than handing the engine an
 * "and take off this many cents" input, is what keeps one formula in one place.
 * A second way to reach a total is the failure this package exists to end.
 */
function withProximityTier(config, tier, percent) {
    if (tier === "none")
        return config;
    return assertValidPricingConfig(Object.assign(Object.assign({}, config), { proximity: Object.assign(Object.assign({}, config.proximity), (tier === "cluster"
            ? { clusterEnabled: true, clusterDiscountPercent: percent }
            : { travelEnabled: true, travelPremiumPercent: percent })) }));
}
exports.withProximityTier = withProximityTier;
/**
 * Rebuild a priceable config from the public projection, on the client.
 *
 * Same-Day and proximity both come back off: the browser holds no surcharge
 * grid, no cutoffs and no notion of who else is booked nearby, so it cannot
 * compute either — it has to ask. Passing `same_day: true` or a proximity tier
 * to the engine with a config built here throws rather than quietly pricing at
 * zero surcharge, which would look to the customer like a bargain and to the
 * server like a mismatched total.
 */
/**
 * The room table a config arrived with, or the flat one when it arrived without.
 *
 * Absent means "sent by a server that predates configurable room prices" — and
 * such a server charges exactly the flat table, so reading it that way reprices
 * nothing. The alternative, refusing the config, would turn an ordinary deploy
 * where the booking app ships a minute before the API into a checkout that
 * cannot load its prices. A table that IS present is validated in full.
 */
function roomPricesOrFlat(prices) {
    return prices !== null && prices !== void 0 ? prices : (0, rates_1.flatRoomPrices)();
}
function fromPublicPricingConfig(published) {
    var _a, _b;
    const effectiveFrom = new Date((_a = published === null || published === void 0 ? void 0 : published.rateCard) === null || _a === void 0 ? void 0 : _a.effectiveFrom);
    if (Number.isNaN(effectiveFrom.getTime())) {
        throw new RangeError(`Published pricing config has an unparseable rate card effectiveFrom: ` +
            `${JSON.stringify((_b = published === null || published === void 0 ? void 0 : published.rateCard) === null || _b === void 0 ? void 0 : _b.effectiveFrom)}`);
    }
    return assertValidPricingConfig({
        rateCard: Object.assign(Object.assign({}, published.rateCard), { effectiveFrom, roomCents: roomPricesOrFlat(published.rateCard.roomCents) }),
        dayOfWeekSurgePercent: Object.assign({}, published.dayOfWeekSurgePercent),
        flexibleDiscountCents: Object.assign({}, published.flexibleDiscountCents),
        sameDaySurchargePercent: rates_1.CLEANING_TYPE_IDS.reduce((acc, id) => {
            acc[id] = perDay(0);
            return acc;
        }, {}),
        sameDay: {
            enabledByDay: perDay(false),
            cutoffMinutesByDay: perDay(0),
            windows: [],
        },
        proximity: noProximityConfig(),
        timeOfDayRules: copyTimeOfDayRules(published.timeOfDayRules),
    });
}
exports.fromPublicPricingConfig = fromPublicPricingConfig;
/**
 * Rehydrate a config that arrived as JSON, and validate it.
 *
 * Clients call this rather than trusting `res.json()`, so a config that a
 * deploy skew or a bad admin edit made invalid fails at the fetch instead of
 * one keystroke later inside a price the customer is looking at.
 */
function parsePricingConfig(serialized) {
    var _a, _b;
    const effectiveFrom = new Date((_a = serialized === null || serialized === void 0 ? void 0 : serialized.rateCard) === null || _a === void 0 ? void 0 : _a.effectiveFrom);
    if (Number.isNaN(effectiveFrom.getTime())) {
        throw new RangeError(`Pricing config rate card has an unparseable effectiveFrom: ${JSON.stringify((_b = serialized === null || serialized === void 0 ? void 0 : serialized.rateCard) === null || _b === void 0 ? void 0 : _b.effectiveFrom)}`);
    }
    return assertValidPricingConfig(Object.assign(Object.assign({}, serialized), { rateCard: Object.assign(Object.assign({}, serialized.rateCard), { effectiveFrom, roomCents: roomPricesOrFlat(serialized.rateCard.roomCents) }) }));
}
exports.parsePricingConfig = parsePricingConfig;
//# sourceMappingURL=config.js.map